import { useCallback, useRef } from "react";
import { Chat, useChat } from "@ai-sdk/react";
import { toAISdkMessages } from "@mastra/ai-sdk/ui";
import { MastraDBMessage } from "@mastra/client-js";
import { DefaultChatTransport, UIMessage } from "ai";

import { useSelectedProject } from "modules/projects/hooks/useSelectedProject";

import { getAIAuthToken } from "modules/chat/utils/authToken";

export type MosaicUIMessage = UIMessage<{ hidden?: boolean }>;

export const useChatThread = ({
  threadId,
  agentId,
  initialProjectIds,
  initialContext
}: {
  threadId: string;
  agentId: string;
  initialProjectIds: string[];
  initialContext?: Record<string, unknown>;
}) => {
  const chatRef = useRef<Chat<MosaicUIMessage>>();
  const mergedMessageIds = useRef(new Set<string>());

  const { rollups } = useSelectedProject();

  if (!chatRef.current) {
    chatRef.current = new Chat<MosaicUIMessage>({
      id: threadId,
      transport: new DefaultChatTransport({
        api: `${import.meta.env.REACT_APP_AI_API_URL}/ai/chat/${agentId}`,
        async prepareSendMessagesRequest({ messages }) {
          const token = await getAIAuthToken();
          const allProjects = rollups.projects.map(project => ({
            id: project.id,
            name: project.name
          }));
          const initialProjects = allProjects
            .filter(project => initialProjectIds.includes(project.id))
            .map(project => ({
              id: project.id,
              name: project.name
            }));

          return {
            headers: {
              Authorization: token
            },
            body: {
              messages: [messages.at(-1)],
              allProjects,
              initialProjects,
              payload: initialContext,
              memory: {
                thread: threadId
              }
            }
          };
        }
      })
    });
  }

  const { setMessages, ...chat } = useChat({ chat: chatRef.current });

  const mergeHistory = useCallback(
    (messages: MastraDBMessage[]) => {
      const newMessages = messages.filter(
        message => !mergedMessageIds.current.has(message.id)
      );
      if (newMessages.length === 0) return;

      newMessages.forEach(message => mergedMessageIds.current.add(message.id));

      setMessages(current => [
        ...(toAISdkMessages(newMessages) as MosaicUIMessage[]),
        ...current
      ]);
    },
    [setMessages]
  );

  const clear = useCallback(() => {
    mergedMessageIds.current = new Set();
    setMessages([]);
  }, [setMessages]);

  return { ...chat, setMessages, mergeHistory, clear };
};
